import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import openings from "./openings.json";

interface JobOpening {
  jobid: string;
  title: string;
  jobtype: string;
  location: string;
  description: string;
  qualification: string;
  requirements: string[];
  apply_link: string;
}

const ApplyJob: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const jobId = searchParams.get("jobid");
  const [job, setJob] = useState<JobOpening | null>(null);

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    message: "",
    cv: null as File | null,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (jobId) {
      const foundJob = (openings as JobOpening[]).find((j) => j.jobid === jobId);
      if (foundJob) {
        setJob(foundJob);
      } else {
        toast({
          title: "Job not found",
          description: "The job you're trying to apply for doesn't exist.",
          variant: "destructive",
        });
        navigate("/human-resource");
      }
    } else {
      toast({
        title: "No job selected",
        description: "Please select a job to apply for.",
        variant: "destructive",
      });
      navigate("/human-resource");
    }
  }, [jobId, navigate, toast]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      // Validate file type and size
      const validTypes = ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!validTypes.includes(file.type)) {
        toast({
          title: "Invalid file type",
          description: "Please upload a PDF or Word document.",
          variant: "destructive",
        });
        e.target.value = "";
        return;
      }

      if (file.size > maxSize) {
        toast({
          title: "File too large",
          description: "Please upload a file smaller than 5MB.",
          variant: "destructive",
        });
        e.target.value = "";
        return;
      }

      setFormData((prev) => ({
        ...prev,
        cv: file,
      }));
    }
  };

  function fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1]; // strip "data:...;base64,"
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }


  async function callApplyForJobs(applicationData: unknown): Promise<{ ok: boolean; message?: string;[key: string]: unknown }> {
    const apiKey = import.meta.env.VITE_API_KEY;
    const API_URL = "https://cbt2it5nbc.execute-api.ap-south-1.amazonaws.com/default/applyforjob";

    const res = await fetch(API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": apiKey,
      },
      body: JSON.stringify(applicationData),
    });

    const data = await res.json();
    return { ok: res.ok, ...data };
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!formData.fullName || !formData.email || !formData.phone || !formData.cv) {
      toast({
        title: "Missing information",
        description: "Please fill in all required fields and attach your CV.",
        variant: "destructive",
      });
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const cvFileBase64 = await fileToBase64(formData.cv!);

      // Prepare form data for AWS Lambda (to be implemented later)
      const applicationData = {
        jobId: job?.jobid,
        jobTitle: job?.title,
        fullName: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        cvFileName: formData.cv.name,
        cvFileSize: formData.cv.size,
        cvFileType: formData.cv.type,
        cvFileBase64: cvFileBase64,
        submittedAt: new Date().toISOString(),
      };

      // Call AWS Lambda API to submit the application
      // const lambdaEndpoint = import.meta.env.VITE_LAMBDA_ENDPOINT || 'YOUR_LAMBDA_ENDPOINT_HERE';

      // const response = await fetch(lambdaEndpoint, {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //   },
      //   body: JSON.stringify(applicationData),
      // });
      const response = await callApplyForJobs(applicationData);

      if (!response.ok) {
        throw new Error(response.message || 'Failed to submit application');
      }

      console.log('Application submitted successfully:', response);

      toast({
        title: "Application submitted!",
        description: "Thank you for applying. We'll review your application and get back to you soon.",
      });

      // Reset form
      setFormData({
        fullName: "",
        email: "",
        phone: "",
        message: "",
        cv: null,
      });

      // Navigate back to careers page after a short delay
      setTimeout(() => {
        navigate("/human-resource");
      }, 2000);
    } catch (error) {
      console.error("Submission error:", error);
      toast({
        title: "Submission failed",
        description: "An error occurred while submitting your application. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!job) {
    return (
      <Layout>
        <main className="container mx-auto px-4 py-24">
          <div className="text-center">
            <p className="text-muted-foreground">Loading job details...</p>
          </div>
        </main>
      </Layout>
    );
  }

  return (
    <Layout>
      <main className="container mx-auto px-4 py-24">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate("/human-resource")}
              className="mb-4"
            >
              {`<- Back to Careers`}
            </Button>

            <h1 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Apply for Position
            </h1>

            <div className="bg-muted border rounded-lg p-6 mb-6">
              <h2 className="text-xl font-semibold mb-2">{job.title}</h2>
              <p className="text-sm text-muted-foreground mb-2">
                {job.location} • {job.jobtype} • Job ID: {job.jobid}
              </p>
              <p className="text-sm text-foreground/80 mb-3">{job.description}</p>
              <div className="mt-3 pt-3 border-t">
                <p className="text-sm font-medium text-foreground/90 mb-1">Qualification:</p>
                <p className="text-sm text-foreground/80">{job.qualification}</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="fullName">
                Full Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="fullName"
                name="fullName"
                type="text"
                placeholder="Please enter your full name"
                value={formData.fullName}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">
                Email Address <span className="text-destructive">*</span>
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="Please enter your email address"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">
                Phone Number <span className="text-destructive">*</span>
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                placeholder="Please enter your phone number"
                value={formData.phone}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">
                Cover Letter / Message
              </Label>
              <Textarea
                id="message"
                name="message"
                placeholder="Tell us why you're a great fit for this role..."
                value={formData.message}
                onChange={handleInputChange}
                className="min-h-[150px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cv">
                Upload CV/Resume <span className="text-destructive">*</span>
              </Label>
              <Input
                id="cv"
                name="cv"
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleFileChange}
                required
                className="cursor-pointer"
              />
              <p className="text-xs text-muted-foreground">
                Accepted formats: PDF, DOC, DOCX (Max size: 5MB)
              </p>
              {formData.cv && (
                <p className="text-sm text-primary">
                  Selected file: {formData.cv.name}
                </p>
              )}
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                type="submit"
                size="lg"
                disabled={isSubmitting}
                className="flex-1"
              >
                {isSubmitting ? "Submitting..." : "Send Application"}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={() => navigate("/human-resource")}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </div>
          </form>

          <div className="mt-8 p-4 bg-muted/50 border rounded-lg">
            <p className="text-sm text-muted-foreground">
              <strong>Note:</strong> Your application will be processed and reviewed by our HR team.
              We typically respond within 1-2 weeks. If you have any questions, please contact us at{" "}
              <a href="mailto:hr@wellnextek.com" className="text-primary underline">
                hr@wellnextek.com
              </a>
            </p>
          </div>
        </div>
      </main>
    </Layout>
  );
};

export default ApplyJob;
